import type { JSX } from 'react';
import { useState } from 'react';
import {
  ActionIcon,
  Alert,
  Anchor,
  Avatar,
  Badge,
  Button,
  Group,
  Menu,
  Modal,
  Select,
  Stack,
  Text,
  TextInput,
  Tooltip,
} from '@mantine/core';
import { useDisclosure } from '@mantine/hooks';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { ROLE_LABELS, ROLE_NAMES, callName, type RoleName } from '@pst/shared';
import { EditableText } from '../ui/EditableText';
import { useContextMenu } from '../ui/ContextMenu';
import { ApiError, api, type IssuedLink, type OpenInvite, type UserSummary } from '../api';
import { formatDate } from '../format';
import { PageHeader, Section, initials } from '../ui/Section';
import { InviteModal, ROLE_HINTS } from './InviteModal';
import { LinkResult } from './LinkResult';
import { useMe } from './useMe';
import { PageScroll } from '../ui/PageScroll';

const ROLE_COLORS: Readonly<Record<RoleName, string>> = {
  admin: 'grape',
  projektleiter: 'brand',
  projektmitarbeiter: 'gray',
};

/**
 * User administration, for the Administration only.
 *
 * One row per person: who, how to reach them, what they may do, whether they
 * can sign in. Accounts are disabled rather than deleted: a disabled account
 * cannot sign in, and nothing that refers to it breaks (deletion: P-08).
 */
export function UsersPage(): JSX.Element {
  const me = useMe();
  const queryClient = useQueryClient();
  const [inviteOpen, inviteDialog] = useDisclosure();
  const [resetFor, setResetFor] = useState<{ user: UserSummary; issued: IssuedLink } | null>(null);

  const users = useQuery({ queryKey: ['users'], queryFn: api.users });
  const invites = useQuery({ queryKey: ['invites'], queryFn: api.invites });

  const refresh = (): void => {
    void queryClient.invalidateQueries({ queryKey: ['users'] });
    void queryClient.invalidateQueries({ queryKey: ['invites'] });
  };
  const update = useMutation({
    mutationFn: ({ id, ...body }: { id: string; role?: RoleName; disabled?: boolean; displayName?: string; nickname?: string | null }) =>
      // The version the list was loaded with: someone else's change in between is refused, not overwritten.
      api.updateUser(id, { ...body, version: queryClient.getQueryData<UserSummary[]>(['users'])?.find((u) => u.id === id)?.version }),
    onSuccess: (_result, variables) => {
      // Renaming oneself shows in the header at once.
      if (variables.id === me?.id) void queryClient.invalidateQueries({ queryKey: ['me'] });
    },
    onSettled: refresh,
  });
  const [deleting, setDeleting] = useState<UserSummary | null>(null);
  const mail = useQuery({ queryKey: ['mail-status'], queryFn: api.mailStatus, staleTime: 60_000 });
  const mailTest = useMutation({ mutationFn: api.mailTest });
  const reset = useMutation({
    mutationFn: ({ user, sendMail }: { user: UserSummary; sendMail: boolean }) =>
      api.resetLink(user.id, sendMail).then((issued) => ({ user, issued })),
    onSuccess: setResetFor,
  });
  const revoke = useMutation({ mutationFn: api.revokeInvite, onSettled: refresh });
  const [resent, setResent] = useState<string | null>(null);
  const resend = useMutation({
    mutationFn: (invite: OpenInvite) => api.resendInvite(invite.id).then(() => invite),
    onSuccess: (invite) => setResent(invite.email),
    onSettled: refresh,
  });
  const error = [update.error, reset.error, revoke.error, resend.error].find((entry) => entry);

  const context = useContextMenu<UserSummary>((user, close) => {
    const self = user.id === me?.id;
    const run = (action: () => void) => () => {
      close();
      action();
    };
    return (
      <>
        <Menu.Label>{user.displayName}</Menu.Label>
        <Menu.Item component="a" href={`mailto:${user.email}`} onClick={close}>E-Mail schreiben</Menu.Item>
        {mail.data?.configured && (
          <Menu.Item onClick={run(() => reset.mutate({ user, sendMail: true }))}>Passwort-Link per E-Mail senden</Menu.Item>
        )}
        <Menu.Item onClick={run(() => reset.mutate({ user, sendMail: false }))}>Link für neues Passwort anzeigen</Menu.Item>
        {!self && (
          <>
            <Menu.Divider />
            {user.disabledAt === null ? (
              <Menu.Item color="critical" onClick={run(() => update.mutate({ id: user.id, disabled: true }))}>Deaktivieren und abmelden</Menu.Item>
            ) : (
              <>
                <Menu.Item onClick={run(() => update.mutate({ id: user.id, disabled: false }))}>Wieder aktivieren</Menu.Item>
                <Menu.Item color="red" onClick={run(() => setDeleting(user))}>Endgültig löschen …</Menu.Item>
              </>
            )}
          </>
        )}
      </>
    );
  });

  const active = (users.data ?? []).filter((u) => u.disabledAt === null);
  const disabled = (users.data ?? []).filter((u) => u.disabledAt !== null);

  return (
    <PageScroll>
    <Stack gap="md" w="100%">
      <PageHeader
        title="Nutzende"
        description="Wer PST nutzen darf und mit welcher Rolle. Welche Projekte jemand sieht, regelt der Reiter „Team“ im Projekt."
        actions={<Button onClick={inviteDialog.open}>Person einladen</Button>}
      />

      <Text size="xs" c="dimmed">
        {mail.data?.configured ? (
          <>
            E-Mail-Versand ist eingerichtet.{' '}
            <Anchor size="xs" component="button" onClick={() => mailTest.mutate()}>
              {mailTest.isPending ? 'sende …' : mailTest.isSuccess ? 'Test-E-Mail an Sie verschickt' : 'Test-E-Mail an mich senden'}
            </Anchor>
            {mailTest.isError && <Text span size="xs" c="red"> – {mailTest.error instanceof ApiError ? mailTest.error.message : 'fehlgeschlagen'}</Text>}
          </>
        ) : (
          'Kein E-Mail-Versand eingerichtet: Einladungs- und Passwortlinks geben Sie selbst weiter (Einrichtung: docs/INSTALL.md, „E-Mail-Versand einrichten“).'
        )}
      </Text>

      {error && (
        <Alert color="red" withCloseButton onClose={() => { update.reset(); reset.reset(); revoke.reset(); resend.reset(); }}>
          {error instanceof ApiError ? error.message : 'Die Änderung ist fehlgeschlagen.'}
        </Alert>
      )}

      <Section title={`Aktiv (${active.length})`}>
        <Stack gap={0}>
          {active.map((user, index) => (
            <UserRow
              key={user.id}
              user={user}
              self={user.id === me?.id}
              first={index === 0}
              onRole={(role) => update.mutate({ id: user.id, role })}
              onDisable={() => update.mutate({ id: user.id, disabled: true })}
              onReset={() => reset.mutate({ user, sendMail: false })}
              onResetMail={mail.data?.configured ? () => reset.mutate({ user, sendMail: true }) : undefined}
              onRename={(body) => update.mutate({ id: user.id, ...body })}
              onContextMenu={(event) => context.open(event, user)}
            />
          ))}
        </Stack>
      </Section>

      <Section
        title="Offene Einladungen"
        description="Ein Link gilt 7 Tage und nur einmal. Sobald das Konto eingerichtet ist, verschwindet die Einladung von hier."
      >
        {resent && (
          <Alert color="green" variant="light" py={6} withCloseButton onClose={() => setResent(null)}>
            Neue Einladung an {resent} gesendet. Der vorige Link gilt nicht mehr.
          </Alert>
        )}
        {invites.isSuccess && invites.data.length === 0 ? (
          <Text size="sm" c="dimmed">Keine offenen Einladungen.</Text>
        ) : (
          <Stack gap={0}>
            {(invites.data ?? []).map((invite, index) => (
              <InviteRow
                key={invite.id}
                invite={invite}
                first={index === 0}
                onRevoke={() => revoke.mutate(invite.id)}
                onResend={mail.data?.configured ? () => resend.mutate(invite) : undefined}
                resending={resend.isPending && resend.variables?.id === invite.id}
              />
            ))}
          </Stack>
        )}
      </Section>

      {disabled.length > 0 && (
        <Section title={`Deaktiviert (${disabled.length})`} description="Kann sich nicht anmelden. Projektzuordnungen bleiben für eine Reaktivierung erhalten.">
          <Stack gap={0}>
            {disabled.map((user, index) => (
              <Group key={user.id} justify="space-between" py="xs" wrap="nowrap" style={index === 0 ? undefined : { borderTop: '1px solid var(--mantine-color-default-border)' }}>
                <Group gap="sm" wrap="nowrap" style={{ minWidth: 0, opacity: 0.6 }}>
                  <Avatar radius="xl" size={36} color="gray">{initials(user.displayName)}</Avatar>
                  <Stack gap={0} style={{ minWidth: 0 }}>
                    <Text size="sm" fw={500} truncate>{user.displayName}</Text>
                    <Text size="xs" c="dimmed" truncate>{user.email} · seit {formatDate(user.disabledAt)}</Text>
                  </Stack>
                </Group>
                <Group gap="xs" wrap="nowrap">
                  <Button size="xs" variant="default" onClick={() => update.mutate({ id: user.id, disabled: false })}>Wieder aktivieren</Button>
                  <Button size="xs" variant="subtle" color="red" onClick={() => setDeleting(user)}>Endgültig löschen …</Button>
                </Group>
              </Group>
            ))}
          </Stack>
        </Section>
      )}

      {deleting && <DeleteUserModal user={deleting} onClose={() => setDeleting(null)} onDone={refresh} />}
      <InviteModal opened={inviteOpen} onClose={inviteDialog.close} />
      {context.menu}
      <Modal opened={resetFor !== null} onClose={() => setResetFor(null)} title="Link für neues Passwort" size="lg">
        {resetFor && (
          <LinkResult
            issued={resetFor.issued}
            purpose={`Mit diesem Link setzt ${resetFor.user.displayName} ein neues Passwort. Das bisherige gilt weiter, bis der Link benutzt wird; danach werden alle Sitzungen des Kontos beendet.`}
            onDone={() => setResetFor(null)}
          />
        )}
      </Modal>
    </Stack>
    </PageScroll>
  );
}

function UserRow({
  user,
  self,
  first,
  onRole,
  onDisable,
  onReset,
  onResetMail,
  onRename,
  onContextMenu,
}: {
  user: UserSummary;
  self: boolean;
  first: boolean;
  onRole: (role: RoleName) => void;
  onDisable: () => void;
  onReset: () => void;
  onResetMail?: () => void;
  onRename: (body: { displayName?: string; nickname?: string | null }) => void;
  onContextMenu: (event: React.MouseEvent) => void;
}): JSX.Element {
  return (
    <Group
      justify="space-between"
      py="sm"
      gap="sm"
      wrap="wrap"
      onContextMenu={onContextMenu}
      style={first ? undefined : { borderTop: '1px solid var(--mantine-color-default-border)' }}
    >
      <Group gap="sm" wrap="nowrap" style={{ minWidth: 0, flex: '1 1 260px' }}>
        <Avatar radius="xl" size={38} color={ROLE_COLORS[user.role]} variant="light">{initials(user.displayName)}</Avatar>
        <Stack gap={0} style={{ minWidth: 0 }}>
          <Group gap={6} wrap="nowrap">
            <EditableText value={user.displayName} bold title="Namen ändern" onCommit={(next) => onRename({ displayName: next })} />
            {self && <Badge size="xs" variant="outline" color="gray" style={{ flexShrink: 0 }}>Sie</Badge>}
          </Group>
          <Group gap={4} wrap="nowrap">
            <Text size="xs" c="dimmed" style={{ flexShrink: 0 }}>Anzeigename</Text>
            <EditableText
              value={user.nickname ?? ''}
              size="xs"
              allowEmpty
              placeholder={`${callName(user.displayName, null)} (Vorname)`}
              title="Anzeigename ändern; leer bedeutet Vorname"
              onCommit={(next) => onRename({ nickname: next || null })}
            />
            <Text size="xs" c="dimmed" truncate>· {user.email}</Text>
          </Group>
        </Stack>
      </Group>
      <Group gap="xs" wrap="nowrap">
        <Tooltip label={self ? 'Die eigene Rolle kann nur eine andere Administration ändern.' : ROLE_HINTS[user.role]} multiline w={260}>
          <Select
            size="xs"
            w={170}
            allowDeselect={false}
            disabled={self}
            aria-label={`Rolle von ${user.displayName}`}
            data={ROLE_NAMES.map((name) => ({ value: name, label: ROLE_LABELS[name] }))}
            value={user.role}
            onChange={(value) => value && value !== user.role && onRole(value as RoleName)}
          />
        </Tooltip>
        <Menu position="bottom-end" withinPortal>
          <Menu.Target>
            <ActionIcon variant="subtle" color="gray" aria-label={`Aktionen für ${user.displayName}`}>{'⋯'}</ActionIcon>
          </Menu.Target>
          <Menu.Dropdown>
            {onResetMail && <Menu.Item onClick={onResetMail}>Passwort-Link per E-Mail senden</Menu.Item>}
            <Menu.Item onClick={onReset}>Link für neues Passwort anzeigen</Menu.Item>
            {!self && (
              <>
                <Menu.Divider />
                <Menu.Item color="critical" onClick={onDisable}>Deaktivieren und abmelden</Menu.Item>
              </>
            )}
          </Menu.Dropdown>
        </Menu>
      </Group>
    </Group>
  );
}

function InviteRow({
  invite,
  first,
  onRevoke,
  onResend,
  resending,
}: {
  invite: OpenInvite;
  first: boolean;
  onRevoke: () => void;
  onResend?: () => void;
  resending: boolean;
}): JSX.Element {
  return (
    <Group justify="space-between" py="xs" gap="sm" wrap="nowrap" style={first ? undefined : { borderTop: '1px solid var(--mantine-color-default-border)' }}>
      <Group gap="sm" wrap="nowrap" style={{ minWidth: 0 }}>
        <Avatar radius="xl" size={36} variant="outline" color="gray">{initials(invite.displayName ?? invite.email)}</Avatar>
        <Stack gap={0} style={{ minWidth: 0 }}>
          <Text size="sm" fw={500} truncate>{invite.displayName}</Text>
          <Text size="xs" c="dimmed" truncate>
            {invite.email}
            {invite.role ? ` · ${ROLE_LABELS[invite.role]}` : ''}
            {invite.projectNumber ? ` · ${invite.projectNumber}` : ''}
          </Text>
        </Stack>
      </Group>
      <Group gap="xs" wrap="nowrap">
        <Text size="xs" c="dimmed" visibleFrom="xs">bis {formatDate(invite.expiresAt)}</Text>
        {onResend && (
          <Tooltip label="Neuer Link per E-Mail, 7 Tage gültig; der vorige Link gilt dann nicht mehr" multiline w={260}>
            <Button size="xs" variant="light" onClick={onResend} loading={resending}>E-Mail erneut senden</Button>
          </Tooltip>
        )}
        <Button size="xs" variant="subtle" color="gray" onClick={onRevoke}>Zurückziehen</Button>
      </Group>
    </Group>
  );
}

/**
 * Deleting an account for good: only a deactivated one, confirmed by typing
 * its address. What stays and what goes is said before, not after.
 */
function DeleteUserModal({ user, onClose, onDone }: { user: UserSummary; onClose: () => void; onDone: () => void }): JSX.Element {
  const [confirm, setConfirm] = useState('');
  const remove = useMutation({
    mutationFn: () => api.removeUser(user.id, confirm.trim()),
    onSuccess: () => { onDone(); onClose(); },
  });
  const matches = confirm.trim().toLowerCase() === user.email.toLowerCase();
  return (
    <Modal opened onClose={onClose} title={`${user.displayName} endgültig löschen`}>
      <form onSubmit={(e) => { e.preventDefault(); if (matches) remove.mutate(); }}>
        <Stack gap="sm">
          {remove.isError && <Alert color="red">{remove.error instanceof ApiError ? remove.error.message : 'Löschen fehlgeschlagen.'}</Alert>}
          <Alert color="red" variant="light">
            Das Konto, seine Projektzuordnungen, offenen Einladungen und die Freigabe des Kennwertkatalogs werden gelöscht.
            Gebuchte Stunden bleiben im Projektbudget, aber ohne Namen („gelöschtes Konto“); bei Basisplänen und Vorlagen
            entfällt „angelegt von“. Rückgängig nur über eine Sicherung.
          </Alert>
          <TextInput label={`Zur Bestätigung die E-Mail-Adresse eingeben: ${user.email}`} value={confirm} onChange={(e) => setConfirm(e.currentTarget.value)} data-autofocus autoComplete="off" />
          <Group justify="flex-end">
            <Button variant="default" onClick={onClose}>Abbrechen</Button>
            <Button type="submit" color="red" loading={remove.isPending} disabled={!matches}>Endgültig löschen</Button>
          </Group>
        </Stack>
      </form>
    </Modal>
  );
}
